<template>
  <div id="gold-spot">
    <div class="graph-area">
      <div v-if="marketStatus && marketStatus !== 'open'" class="market-status">
        <i class="fas fa-do-not-enter"></i>
        ตลาดปิด
      </div>
      <div v-if="loading" class="skeleton-card" style="position: absolute;">
        <div class="box"></div>
      </div>
      <svg :id="goldSpotId" width="100%">
        <!-- <defs>
          <pattern id="pattern1" x="10" y="10" width="10" height="10" patternUnits="userSpaceOnUse" />
        </defs>-->
      </svg>
    </div>
    <div class="period-area">
      <PeriodNavigation
        backgorund="#ffffff"
        activePosition="top"
        data-action="change_gold_spot_period"
        :arrPeriod="getGoldSpotRealtimePeriod.range"
        :alias="`period`"
        @select-data="choosePeriod"
        :defaultItem="getGoldSpotRealtimePeriod.default"
        :slidePerPage="9"
      />
    </div>
    <!-- <div class="professional-btn-center">
      <a
        class="ctn-graph-professional"
        data-action="gold_spot_trading_view"
        href="javascript:;"
        @click="callProfessionalGraph"
      >
        <i class="fas fa-chart-bar"></i> กราฟ Real-time
      </a>
    </div>-->
  </div>
</template>

<script>
import * as d3 from 'd3'
import { mapGetters, mapActions } from 'vuex'
import { timestampToDate, findPreviousDate, findNextTime, convertHex } from '../../../utils/global'
import { formatCurrency } from '../../../utils/global'

const PeriodNavigation = () => import('../global/PeriodNavigation')

let periodCurrentTick = ['1m', '5m']
let periodSnapshot = ['15m', '1h', '12h', '1D']
let periodAggregate = ['1W', '1M', '3M', '6M', 'YTD', '1Y', '5Y', '10Y', 'MAX']

let xRange, yRange

// Helper Functions
let helpers = {
  TH: d3.timeFormatDefaultLocale({
    decimal: '.',
    thousands: ',',
    grouping: [3],
    currency: ['$', ''],
    dateTime: '%a %b %e %X %Y',
    date: '%d/%m/%Y',
    time: '%H:%M:%S',
    periods: ['AM', 'PM'],
    days: ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
    shortDays: ['อ', 'จ', 'อ', 'พ', 'พฤ', 'ศ', 'ส'],
    months: [
      'มกราคม',
      'กุมภาพันธ์',
      'มีนาคม',
      'เมษายน',
      'พฤษภาคม',
      'มิถุนายน',
      'กรกฎาคม',
      'สิงหาคม',
      'กันยายน',
      'ตุลาคม',
      'พฤศจิกายน',
      'ธันวาคม'
    ],
    shortMonths: ['ม.ค.', 'ก.พ.', 'มี.ค.', 'เม.ย.', 'พ.ค.', 'มิ.ย.', 'ก.ค.', 'ส.ค.', 'ก.ย.', 'ต.ค.', 'พ.ย.', 'ธ.ค.']
  }),
  numberFormat: function(num) {
    var parts = num.toString().split('.')
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    return `$ ${parts.join('.')}`
  },
  announcementFormate: function(num, period) {
    return period == '1D' ? `ประกาศครั้งที่ ${num}` : `ประกาศล่าสุดของวัน`
  },
  changeFormat: change => {
    let priceChange = parseFloat(change).toFixed(2)
    if (priceChange > 0) return `+${priceChange}%`
    return `${priceChange}%`
  },
  //   parseDate: d3.timeParse("%Y-%m-%dT%H:%M:%SZ"),
  parseDate: d3.timeParse('%Y-%m-%d %hh:%mm:%ss'),
  getDate: d3.timeFormat('%d/%m/%y | %H:%M:%S'),
  colorTheme: function(pl) {
    if (pl > 0) return constants.profit
    else if (pl == 0) return constants.stable
    return constants.loss
  },
  iconPriceChange: function(pl) {
    if (pl > 0) return '\uf0d8'
    else if (pl == 0) return '\uf0dc'
    return '\uf0d7'
  }
}

// Constant
let constants = {
  graphMarginPercentage: 0.1,

  // color and styling
  grey: '#B0B0B0',
  profit: '#17c161',
  loss: '#da1616', //da1616
  stable: '#999999',
  graphColor: '#3fc0bf',
  priceToolipColor: 'rgba(63, 192, 191, 1)',

  margin: {
    top: 20,
    left: 0,
    right: 0,
    bottom: 20,
    bgTop: 12
  },

  // offset
  offsetRightY: 70,

  // Focusing
  focusStroke: 1.5,
  lineStroke: 2,
  axisStroke: 2,
  circleRadius: 4,

  // Tooltip
  tooltipWidth: 150,
  tooltipHeight: 60,
  tooltipRealtimePriceWidth: 60,
  tooltipRealtimePriceHeight: 20,

  driftRightMargin: 10,
  driftTopMargin: 22.5,

  // Tick Styling
  tickMoveFactor: 34
}

export default {
  components: {
    PeriodNavigation
  },
  props: {
    goldSpotId: {
      type: String,
      default: 'gold-spot-graph-d3-line'
    }
  },
  data() {
    return {
      firstPaint: true,
      loading: false,
      lastPrice: [],
      countTime: null,
      timeInterval: null,
      sockets: {},
      socketData: [],
      marketStatus: null
    }
  },
  computed: {
    ...mapGetters('gold', [
      'getGoldSpotRealtimeGraph',
      'getGoldSpotRealtimeCurrentPeriod',
      'getGoldSpotRealtimePeriod',
      'getGoldGlobalConfig',
      'getGoldSpotLastQuote'
    ]),
    limitDataPoint() {
      let minute = 120
      switch (this.getGoldSpotRealtimeCurrentPeriod) {
        case '1m':
          return minute
        case '5m':
          return minute * 5
        case '15m':
          return minute * 15
        case '1h':
          return minute * 60
        case '12h':
          return minute * 60 * 12
        case '1D':
          return minute * 60 * 24
        default:
          return 120 // 1 minute
      }
    },
    formatTitle() {
      switch (this.getGoldSpotRealtimeCurrentPeriod) {
        case '1m':
          return helpers.TH.format('%H:%M:%S')
        case '5m':
          return helpers.TH.format('%H:%M')
        case '15m':
          return helpers.TH.format('%H:%M')
        case '1h':
          return helpers.TH.format('%H:%M')
        case '12h':
          return helpers.TH.format('%H:%M')
        case '1D':
          return helpers.TH.format('%H:%M')
        case '1W':
          return helpers.TH.format('%e %b')
        case '1M':
          return helpers.TH.format('%e %b')
        case '3M':
          return helpers.TH.format('%e %b')
        case '6M':
          return helpers.TH.format('%e %b')
        case 'YTD':
          return helpers.TH.format('%e %b')
        case '1Y':
          return helpers.TH.format('%b %y')
        case '5Y':
          return helpers.TH.format('%Y')
        case '10Y':
          return helpers.TH.format('%Y')
        case 'MAX':
          return helpers.TH.format('%Y')
        default:
          return helpers.TH.format('%e %b %y')
      }
    },
    formatTimeScale() {
      switch (this.getGoldSpotRealtimeCurrentPeriod) {
        case '1m':
          return d3.timeSecond.every(15)
        case '5m':
          return d3.timeMinute.every(1)
        case '15m':
          return d3.timeMinute.every(3)
        case '1h':
          return d3.timeMinute.every(10)
        case '12h':
          return d3.timeHour.every(2)
        case '1D':
          return d3.timeHour.every(4)
        case '1W':
          return d3.timeDay.every(1)
        case '1M':
          return d3.timeWeek.every(1)
        case '3M':
          return d3.timeDay.every(16)
        case '6M':
          return d3.timeMonth.every(1)
        case 'YTD':
          return d3.timeWeek.every(2)
        case '1Y':
          return d3.timeMonth.every(2)
        case '5Y':
          return d3.timeYear.every(1)
        case '10Y':
          return d3.timeYear.every(2)
        case 'MAX':
          return d3.timeYear.every(2)
        default:
          return 4
      }
    }
  },
  async created() {
    const ns = await this.$nextTick()
    ns ? this.clearGoldSpotRealtimeGraph() : null
  },
  async mounted() {
    this.loading = !this.loading
    this.marketStatus = await this.fetchMarketStatus('currency')
    await this.queryData()
    this.loading = !this.loading
  },
  beforeDestroy() {
    clearInterval(this.timeInterval)
    this.disconnectSocket() // disconnect socket
  },
  destroyed() {
    if (this.sockets && this.sockets.readyState === 2) this.sockets.onclose = () => {} // close connection from server
  },
  methods: {
    ...mapActions('gold', [
      'updateGoldSpotRealtimeGraph',
      'updateGoldSpotAggregateGraph',
      'clearGoldSpotRealtimeGraph',
      'fetchGoldSpotSnapshot',
      'fetchGoldSpotAggregate',
      'findGoldSpotLastQuote'
    ]),
    ...mapActions('global', ['fetchMarketStatus']),
    async queryData() {
      await this.clearGoldSpotRealtimeGraph()
      periodAggregate.includes(this.getGoldSpotRealtimeCurrentPeriod)
        ? await this.getAggregate()
        : await this.getSnapshot()
      await this.appendLatestTick()
      await this.renderGraph()
      if (this.marketStatus === 'open') {
        await this.connectSocket() // connect socket
        this.getSocket()
        if (periodSnapshot.includes(this.getGoldSpotRealtimeCurrentPeriod)) this.updateWithInterval()
      }
    },
    connectSocket() {
      var loc = window.location,
        uri = 'ws:'
      if (loc.protocol === 'https:') uri = 'wss:'

      uri += '//' + loc.host
      uri += '/ws/market/?transport=websocket&channels=currency'

      this.sockets = new WebSocket(uri)
    },
    disconnectSocket() {
      if (this.sockets && this.sockets.readyState === 1) this.sockets.close()
    },
    async choosePeriod(period) {
      if (period == this.getGoldSpotRealtimeCurrentPeriod) return 0
      if (this.loading) return 0
      this.getGoldSpotRealtimePeriod.default = period
      this.$store.state.gold.goldSpotRealtimeCurrentPeriod = period
      clearInterval(this.timeInterval)
      await this.disconnectSocket() // disconnect socket
      this.loading = !this.loading
      await this.queryData()
      this.loading = !this.loading
    },
    updateLastPrice(arrData) {
      if (arrData && arrData.length) this.$store.state.gold.goldSpotLastPrice = arrData[0].b // update last price
    },
    updatePriceLabel(arrData) {
      let vis = d3.select(`#${this.goldSpotId}`)
      // Update position (price tooltip)
      vis.selectAll('.price-tooltip-rect').attr('y', () => {
        return yRange(arrData[0].b)
      })
      // Update position and last price (nav price tooltip)
      vis
        .selectAll('.price-tooltip-nav')
        .attr('y', () => {
          return yRange(arrData[0].b)
        })
        .text(formatCurrency(arrData[0].b, 'USD', 3))
    },
    updateWithInterval() {
      // Set the date we're counting down to
      this.countTime = findNextTime(this.getGoldSpotRealtimeCurrentPeriod, this.getGoldGlobalConfig)
      // Update the count down every 1 second
      this.timeInterval = setInterval(async () => {
        // Get today's date and time
        var now = new Date().getTime()

        // Find the distance between now and the count down date
        var distance = this.countTime - now

        // Time calculations for days, hours, minutes and seconds
        // var days = Math.floor(distance / (1000 * 60 * 60 * 24))
        // var hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
        // var minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60))
        // var seconds = Math.floor((distance % (1000 * 60)) / 1000)

        // Output the result in an element with id="demo"
        // this.time = days + "d " + hours + "h " + minutes + "m " + seconds + "s "

        // If the count down is over, write some text
        if (distance <= 0) {
          this.countTime = findNextTime(this.getGoldSpotRealtimeCurrentPeriod, this.getGoldGlobalConfig)
          this.lastPrice && this.lastPrice.length ? (this.lastPrice[0].t = now) : null // set time form interval
          await this.updateGoldSpotRealtimeGraph(this.lastPrice)
          this.renderGraph()
        }
      }, 1000)
    },
    updateYAxis(data) {
      if (this.socketData.length > 30) this.socketData.shift()
      this.socketData.push(data[0].b)
      this.drawGraph({ isFirst: false })
    },
    findTimeMoreThanLatest(item) {
      return item.filter(item => item.t > this.getGoldSpotRealtimeGraph[this.getGoldSpotRealtimeGraph.length - 1].t)
    },
    async getAggregate() {
      try {
        const prevDate = await findPreviousDate(this.getGoldSpotRealtimeCurrentPeriod)
        const gcsConfig = this.getGoldGlobalConfig
          .filter(item => item.period == this.getGoldSpotRealtimeCurrentPeriod)
          .map(item => ({
            multiplier: item.multiplier,
            timespan: item.timespan
          }))[0]
        const data = {
          multiplier: gcsConfig.multiplier,
          timespan: gcsConfig.timespan,
          from: prevDate,
          to: timestampToDate(Date.now(), 'YYYY-MM-DD', 'global')
        }
        let goldSpotAggr = await this.fetchGoldSpotAggregate(data)
        await this.updateGoldSpotAggregateGraph(goldSpotAggr)
      } catch (error) {
        throw new Error(error)
      }
    },
    async getSnapshot() {
      try {
        const response = await this.fetchGoldSpotSnapshot({
          period: this.getGoldSpotRealtimeCurrentPeriod,
          marketStatus: this.marketStatus
        })
        await this.updateGoldSpotRealtimeGraph(response)
      } catch (error) {
        throw new Error(error)
      }
    },
    getSocket() {
      try {
        this.sockets.onmessage = async evt => {
          let data = JSON.parse(evt.data)
          data = data.filter(item => item.p == 'XAU/USD') // filter type of curency
          data = await this.findTimeMoreThanLatest(data)
          if (data.length) {
            this.lastPrice = data
            if (periodCurrentTick.includes(this.getGoldSpotRealtimeCurrentPeriod)) {
              await this.updateGoldSpotRealtimeGraph(this.lastPrice)
              this.renderGraph()
            }
            if (periodAggregate.includes(this.getGoldSpotRealtimeCurrentPeriod)) {
              this.updateYAxis(this.lastPrice)
            }
            this.updateLastPrice(this.lastPrice)
            this.updatePriceLabel(this.lastPrice)
          }
        }
      } catch (error) {
        this.connectSocket() // reconnect socket
        throw new Error(error)
      }
    },
    async appendLatestTick() {
      await this.findGoldSpotLastQuote({from: "XAU", to: "USD"})
      const lastQuote = this.getGoldSpotLastQuote
      const goldSpotRealtimeGraph = this.getGoldSpotRealtimeGraph
      if (lastQuote && goldSpotRealtimeGraph.length > 0 && lastQuote.timestamp > goldSpotRealtimeGraph[goldSpotRealtimeGraph.length-1].t) {
        if (periodAggregate.includes(this.getGoldSpotThaiRealtimeCurrentPeriod)) {
          await this.updateGoldSpotAggregateGraph([{c: lastQuote.bid, t: lastQuote.timestamp}])
        } else {
          await this.updateGoldSpotRealtimeGraph([{b: lastQuote.bid, t: lastQuote.timestamp}])
        }
      }
    },
    graphHeight() {
      let graphWidth = document.getElementById(this.goldSpotId).clientWidth
      let graphRatio = graphWidth > 760 ? 0.5 : 0.6

      return graphWidth * graphRatio
    },
    renderGraph() {
      if (this.getGoldSpotRealtimeGraph.length === 0) return 0
      if (d3.select(`#${this.goldSpotId}`).empty()) return 0

      if (this.firstPaint) {
        this.drawGraph({ isFirst: true })
        this.firstPaint = false
      } else {
        this.drawGraph({ isFirst: false })
      }
    },
    calyRangeDomain(data, yMax, yMin) {
      if (periodAggregate.includes(this.getGoldSpotRealtimeCurrentPeriod) && this.socketData.length) {
        let minSocketRange = d3.min(this.socketData, d => Number(d))
        let maxSocketRange = d3.max(this.socketData, d => Number(d))
        yMax = maxSocketRange > yMax ? maxSocketRange : yMax
        yMin = minSocketRange < yMin ? minSocketRange : yMin
      }
      let diff = (yMax - yMin) / 10
      return [yMax + diff, yMin - diff]
    },
    calxRangeDomain(data, limit, duration) {
      if (
        periodSnapshot.includes(this.getGoldSpotRealtimeCurrentPeriod) ||
        periodCurrentTick.includes(this.getGoldSpotRealtimeCurrentPeriod)
      ) {
        return [data[data.length - 1].t - limit * duration, data[data.length - 1].t - duration]
      } else {
        return [d3.min(data, d => Number(d.t)), d3.max(data, d => Number(d.t))]
      }
    },
    drawGraph(options) {
      let isFirst = options.isFirst
      // Preparing Data
      let formattedLineData = this.getGoldSpotRealtimeGraph

      let vis = d3.select(`#${this.goldSpotId}`)
      let spaceNavLineElem = document.getElementById(this.goldSpotId)

      // change format date
      // formattedLineData.forEach(d => {
      //   d.t = new Date(d.t)
      // })

      // Prepare range and domain
      // Max and min in x bar, y bar
      let svgWidth, svgHeight
      let xAxis, yAxis
      // let graphMargin
      let yMin = d3.min(formattedLineData, d => Number(d.v))
      let yMax = d3.max(formattedLineData, d => Number(d.v))
      let duration = 500 // how quickly to move (will look jerky if less that data input rate)
      let limit = this.limitDataPoint // how many datapoints, total points = (duration * limit) 1 minute = 120

      // manage text in ticks
      let formatCurrency = d => {
        let toNumber = parseFloat(d)
          .toLocaleString('th', {
            minimumFractionDigits: 3,
            maximumFractionDigits: 4
          })
          .replace(/\B(?=(\d{3})+(?!\d))/g, ',')
        return `$ ${toNumber}`
      }

      let sizeUpdate = () => {
        let clientWidth = document.getElementById(this.goldSpotId).clientWidth
        svgWidth = clientWidth ? clientWidth : 0
        svgHeight = this.graphHeight()
        spaceNavLineElem.style.height = svgHeight + 'px'
      }

      let rangeUpdate = () => {
        xRange = d3
          .scaleTime()
          .range([constants.margin.left + 10, svgWidth - constants.margin.right - constants.offsetRightY, 2])
          .domain(this.calxRangeDomain(formattedLineData, limit, duration))

        // graphMargin = (yMax * 0.5) / 100
        // graphMargin = (yMax - yMin) * constants.graphMarginPercentage

        yRange = d3
          .scaleLinear()
          .range([constants.margin.bottom, svgHeight - constants.margin.top])
          .domain(this.calyRangeDomain(formattedLineData, yMax, yMin))
      }

      let axisUpdate = () => {
        let tickCount = svgWidth / 50
        xAxis = d3
          .axisBottom(xRange)
          .tickFormat(this.formatTitle)
          .ticks(this.formatTimeScale)

        yAxis = d3
          .axisRight(yRange)
          .tickFormat(formatCurrency)
          .ticks(tickCount)
      }

      sizeUpdate()
      rangeUpdate()
      axisUpdate()

      // Appending or Changing Element
      let lineGen = d3
        .line()
        .curve(d3.curveLinear) // http://bl.ocks.org/d3indepth/b6d4845973089bc1012dec1674d3aff8
        .x(d => xRange(d.t))
        .y(d => yRange(d.v))

      // y Axis
      var make_y_axis = function() {
        let tickCount = svgWidth / 50

        return d3.axisRight(yRange).ticks(tickCount)
      }

      var generateTooltip = () => {
        // Tooltip
        let tooltip = vis
          .append('g')
          .attr('class', 'focus-tooltip')
          .style('display', 'none')

        // Tooltip background
        tooltip
          .append('rect')
          .attr('class', 'tooltip-rect')
          .attr('width', constants.tooltipWidth)
          .attr('height', constants.tooltipHeight)
          .attr('rx', 4)
          .attr('ry', 4)

        // Tooltip date
        tooltip
          .append('text')
          .attr('x', 40)
          .attr('y', 20)
          .attr('class', 'tooltip-date')
          .attr('text-anchor', 'left')
          .attr('fill', constants.grey)
          .text('12/03/07')

        // Tooltip icon
        tooltip
          .append('text')
          .attr('class', 'tooltip-logo')
          .attr('x', 25)
          .attr('y', 20)
          .attr('fill', constants.grey)
          .attr('font-family', '"Font Awesome 5 Pro"')
          .attr('font-size', '10px')
          .text('\uf017')

        // Tooltip circle icon
        tooltip
          .append('text')
          .attr('class', 'tooltip-logo')
          .attr('x', 10)
          .attr('y', 40)
          .attr('fill', constants.graphColor)
          .attr('stroke', constants.graphColor)
          .attr('stroke-width', constants.circleRadius)
          .attr('font-family', '"Font Awesome 5 Pro"')
          .attr('font-size', '4px')
          .text('\uf111')

        // Tooltip nav data
        tooltip
          .append('text')
          .attr('x', 20)
          .attr('y', 42)
          .attr('class', 'tooltip-nav')
          .attr('text-anchor', 'left')
          .text('10.2464')

        // Tooltip percent change
        tooltip
          .append('text')
          .attr('x', 100)
          .attr('y', 42)
          .attr('class', 'tooltip-change')
          .attr('text-anchor', 'left')
          .text('0')
      }

      var generatePriceTooltip = () => {
        let tooltip = vis.append('g').attr('class', 'price-tooltip')

        tooltip
          .append('rect')
          .attr('class', 'price-tooltip-rect')
          .attr('width', constants.tooltipRealtimePriceWidth)
          .attr('height', constants.tooltipRealtimePriceHeight)
          .attr('rx', 1)
          .attr('ry', 1)
          .data(formattedLineData)
          .attr('x', d => xRange(d.t))
          .attr('y', d => yRange(d.v))
          .attr('fill', constants.priceToolipColor)
          .attr('transform', `translate(10,-10)`)

        // Tooltip nav data
        tooltip
          .append('text')
          .attr('class', 'price-tooltip-nav')
          .attr('text-anchor', 'left')
          .data(formattedLineData)
          .attr('x', d => xRange(d.t))
          .attr('y', d => yRange(d.v))
          .attr('transform', `translate(15,5)`)
          .attr('fill', 'white')
          .style('font-size', '10px')
      }

      if (isFirst) {
        // Create x axis
        vis
          .append('svg:g')
          .attr('class', 'x axis')
          .call(xAxis)

        // Create y axis
        vis
          .append('svg:g')
          .attr('class', 'y axis')
          .attr('transform', `translate(${svgWidth - 90},-10)`)
          .attr('fill', 'none')
          .call(yAxis)

        // Create line in graph y axis
        vis
          .append('g')
          .attr('class', 'grid')
          .style('opacity', '.2')
          .attr('transform', `translate(-${constants.offsetRightY},0)`)
          .attr('stroke-dasharray', '3,3')
          .call(
            make_y_axis()
              .tickSize(0 + svgWidth, 0, 0)
              .tickFormat('')
          )

        // Data Line
        vis
          .append('path')
          .attr('class', 'line-data')
          .transition()
          .duration(duration)
          .ease(d3.easeLinear, 2)
          .attr('d', lineGen(formattedLineData))
          .attr('stroke', constants.graphColor)
          .attr('stroke-width', constants.lineStroke)
          .attr('fill', 'none')

        // Create circle in data point into graph
        vis
          .append('svg')
          .selectAll('circle')
          .data(formattedLineData)
          .enter()
          .append('circle')
          .attr('cx', d => xRange(d.t))
          .attr('cy', d => yRange(d.v))
          .attr('class', 'price-circle')

        // Create custom in last circle
        vis
          .selectAll('.price-circle:last-child')
          .attr('fill', convertHex(constants.graphColor, 100))
          .attr('stroke', convertHex(constants.graphColor, 50))
          .attr('stroke-width', constants.circleRadius + 8)
          .attr('r', constants.circleRadius)
          .attr('fill', constants.graphColor)
          .attr('class', 'blink')

        // Create price tooltip
        generatePriceTooltip()

        // focus Tooltip
        generateTooltip()

        // Line on focus
        vis
          .append('line')
          .attr('class', 'focus-line')
          .attr('y1', constants.margin.top)
          .attr('y2', svgHeight - constants.margin.bottom)
          .attr('stroke-width', constants.focusStroke)
          .attr('stroke', '#999999')
          .style('display', 'none')

        // Circle
        let focusCircle = vis
          .append('g')
          .attr('class', 'focus-circle')
          .style('display', 'none')

        // Circle Background
        focusCircle.append('circle').attr('r', constants.circleRadius + 3)

        // Circle Inside
        focusCircle
          .append('circle')
          .attr('class', 'inner-circle')
          .attr('r', constants.circleRadius)
      } else {
        // X Axis
        vis
          .select('.x')
          .call(xAxis)
          .transition()
          .duration(duration)
          .ease(d3.easeLinear, 2)

        vis.selectAll('.focus-line').style('display', 'none')

        vis.selectAll('.focus-circle').style('display', 'none')

        vis.selectAll('.focus-tooltip-day').style('display', 'none')

        vis.selectAll('.focus-tooltip').style('display', 'none')

        // y Axis
        vis
          .select('.y')
          .attr('fill', 'none')
          .call(yAxis)

        // grid
        vis
          .select('.grid')
          .attr('class', 'grid')
          .style('opacity', '.2')
          .attr('transform', `translate(-${constants.offsetRightY},0)`)
          .call(
            make_y_axis()
              .tickSize(0 + svgWidth, 0, 0)
              .tickFormat('')
          )

        // Data Line
        vis
          .select('.line-data')
          .transition()
          .duration(duration)
          .ease(d3.easeLinear, 2)
          .attr('d', lineGen(formattedLineData))
          .attr('stroke', constants.graphColor)
          .attr('fill', 'none')
          .attr('transform', null)
      }

      d3.selectAll('.domain').style('display', 'none')

      // Handling mouse event for focus tooltip

      //cache for performance
      let elemFocusLine = vis.select('.focus-line')
      let elemFocusCircle = vis.select('.focus-circle')
      let elemFocusTooltip = vis.select('.focus-tooltip')
      let elemFocusTooltipDate = vis.selectAll('.tooltip-date')
      let elemFocusTooltipNav = vis.selectAll('.tooltip-nav')
      let elemFocusTooltipChange = vis.select('.tooltip-change')
      let elemFocusTooltipChangeIcon = vis.select('.tooltip-change-icon')

      // Helpers function
      let mouseEvent = {
        bisectDate: () =>
          d3.bisector(function(d) {
            return d.t
          }).left,
        mouseOver: () => {
        },
        mouseOut: () => {
          elemFocusLine.style('display', 'none')
          elemFocusCircle.style('display', 'none')
          elemFocusTooltip.style('display', 'none')
        },
        mouseMove: function() {
          mouseEvent.handleMove(d3.mouse(this)[0], d3.mouse(this)[1])
        },
        touchMove: function() {
          mouseEvent.handleMove(d3.touches(this)[0][0], d3.touches(this)[0][1])
        },
        handleMove: (xPos, yPos) => {
          
          let x0 = xRange.invert(xPos),
            i = mouseEvent.bisectDate()(formattedLineData, x0, 1),
            d0 = formattedLineData[i - 1],
            d1 = formattedLineData[i]

          if (d0 != null && d1 != null) {
            let d = x0 > d0.d ? d1 : d0
            if (xRange(d0.d) == 0 && xRange(x0) < xRange(d1.d) / 2) {
              d = d0
            }

            elemFocusLine.style('display', null)
            elemFocusCircle.style('display', null)
            elemFocusTooltip.style('display', null)
            elemFocusLine.attr('transform', 'translate(' + xRange(d.t) + ',' + 0 + ')')
            elemFocusCircle
              .attr('transform', 'translate(' + xRange(d.t) + ',' + yRange(d.v) + ')')
              .attr('fill', constants.graphColor)
            // Tooltip move
            // Modify padding for displaying
            let xPad = 0,
              yPad = 0
            if (svgWidth - xRange(d.t) < constants.tooltipWidth) {
              xPad = -constants.tooltipWidth - constants.driftRightMargin * 2
            }

            if (svgHeight - constants.margin.bottom - constants.margin.bgTop - yRange(d.v) < constants.tooltipHeight) {
              yPad = -constants.tooltipHeight / 2
            }

            if (yRange(d.v) < constants.tooltipHeight) {
              yPad = constants.tooltipHeight / 2
            }

            // set tooltip price format
            elemFocusTooltipNav.text(helpers.numberFormat(d.v))
            // set tooltip date format
            elemFocusTooltipDate.text(helpers.getDate(d.t))
            // set tooltip color price change
            elemFocusTooltipChange.text(helpers.changeFormat(d.p)).attr('fill', helpers.colorTheme(d.p))
            elemFocusTooltipChangeIcon.text(helpers.iconPriceChange(d.p)).attr('fill', helpers.colorTheme(d.p))

            // elemFocusTooltip.attr("transform", "translate(" + xRange(d.t+xPad) + "," + yRange(d.v+yPad) + ")")

            elemFocusTooltip.attr(
              'transform',
              'translate(' +
                (xRange(d.t) + xPad + constants.driftRightMargin) +
                ',' +
                (yRange(d.v) + yPad - constants.driftTopMargin) +
                ')'
            )
          }
        }
      }

      if (!isFirst) {
        vis.select('.focusing-overlay').remove()
      }

      // Overlay Element
      vis
        .append('rect')
        .attr('class', 'focusing-overlay')
        .attr('fill', 'none')
        .attr('pointer-events', 'all')
        .attr('y', constants.margin.bgTop)
        .attr('width', svgWidth)
        .attr('height', svgHeight - constants.margin.bottom - constants.margin.bgTop)
        .on('mouseover', mouseEvent.mouseOver)
        .on('mouseout', mouseEvent.mouseOut)
        .on('mousemove', mouseEvent.mouseMove)
        .on('touchmove', mouseEvent.touchMove, true)

      // Styling
      // First and last tick
      let tickUpdate = () => {
        var fontSize
        if (svgWidth < 768) {
          fontSize = '10px'
        } else {
          fontSize = '14px'
        }
        vis
          .selectAll('.tick text')
          .attr('x', constants.tickMoveFactor)
          .attr('y', 10)
          .attr('font-family', '"DBHeaventRez"')
          .style('font-size', fontSize)
          .attr('fill', constants.grey)

        vis.selectAll('.x .tick text').attr('x', 0)
        // vis.selectAll(".x .tick:nth-of-type(2) text").attr("x", 0);

        vis.selectAll('.x .tick text').attr('y', this.graphHeight() - 15)

        vis.selectAll('.y .tick text').style('font-size', fontSize)

        vis.selectAll('.y').attr('transform', `translate(${svgWidth - 90},-10)`)

        vis.selectAll('.grid').attr('transform', `translate(-${constants.offsetRightY},0)`)

        vis
          .selectAll('.x-axis-line')
          .attr('x1', 0)
          .attr('x2', svgWidth)
          .attr('y1', svgHeight)
          .attr('y2', svgHeight)
          .style('font-size', fontSize)

        vis
          .selectAll('.focus-line')
          .attr('y1', constants.margin.top)
          .attr('y2', svgHeight)

        // Update last circle position
        vis
          .selectAll('.blink')
          .attr('cx', () => {
            return xRange(formattedLineData[formattedLineData.length - 1].d)
          })
          .attr('cy', () => {
            return yRange(formattedLineData[formattedLineData.length - 1].v)
          })
          .transition()
          .duration(duration)
          .ease(d3.easeLinear, 2)

        // Update position (price tooltip)
        vis
          .selectAll('.price-tooltip-rect')
          .attr('x', () => {
            return xRange(formattedLineData[formattedLineData.length - 1].d)
          })
          .attr('y', () => {
            return yRange(formattedLineData[formattedLineData.length - 1].v)
          })
        // Update position and last price (nav price tooltip)
        vis
          .selectAll('.price-tooltip-nav')
          .attr('x', () => {
            return xRange(formattedLineData[formattedLineData.length - 1].d)
          })
          .attr('y', () => {
            return yRange(formattedLineData[formattedLineData.length - 1].v)
          })
          .text(() => {
            return formatCurrency(formattedLineData[formattedLineData.length - 1].v, 'USD', 3)
          })

        document.getElementById(this.goldSpotId).style.height = this.graphHeight() + 'px'
      }

      tickUpdate()

      // Inner circle focusing
      vis.select('.inner-circle').attr('fill', 'white')

      // Responsive Event for graph and axis
      let updateAxisAndGraph = () => {
        vis.select('.x').call(xAxis)

        vis
          .select('.y')
          .attr('fill', 'none')
          .attr('transform', 'translate(-20, 0)')
          .call(yAxis)

        // Update Position and Data Line
        vis.select('.line-data').attr('d', lineGen(formattedLineData))

        // Update Position and Data circle
        vis
          .selectAll('.price-circle')
          .data(formattedLineData)
          .attr('cx', function(d) {
            return xRange(d.t)
          })
          .attr('cy', function(d) {
            return yRange(d.v)
          })
        // Hide Position and Data circle
        // vis.selectAll(".price-circle").attr("display", "none")

        vis
          .select('.grid')
          .attr('class', 'grid')
          .style('opacity', '.2')
          .attr('transform', 'translate(50, 0)')
          .call(
            make_y_axis()
              .tickSize(0 + svgWidth, 0, 0)
              .tickFormat('')
          )
      }

      if (!isFirst) {
        d3.select(window).on('resize', null)
      }

      d3.select(window).on('resize', () => {
        sizeUpdate()
        rangeUpdate()
        axisUpdate()
        updateAxisAndGraph()
        tickUpdate()
      })
    },
    callProfessionalGraph() {
      this.$emit('call-tradingview', true)
    }
  }
}
</script>

<style lang="scss">
#gold-spot {
  .graph-area {
    position: relative;
    height: 100%;
    margin-top: 12px;
    .market-status {
      color: $color-minus;
      @extend %font-size-ss;
      i {
        font-size: 12px;
      }
    }
  }
  .professional-btn-center {
    margin: 20px 0 25px 0;
    text-align: center;
    .ctn-graph-professional {
      @include finRippleBtn(#f4f4f4, #f4f4f4, $color-normal-text);
      @extend %font-size-ss;
      padding: 5px 19px;
      text-decoration: none;
      font-weight: bold;
      border-radius: 4px;
      i {
        pointer-events: none;
        margin-right: 4px;
      }
    }
  }
  .tooltip-rect {
    fill: #ffffff;
    fill-opacity: 1;
    stroke: rgba(186, 186, 186, 0.24);
    stroke-width: 1px;
  }
  .tooltip-nav {
    fill: #444444;
    @extend %font-size-sss;
    font-weight: bold;
    text-anchor: left;
  }
  .tooltip-change {
    @extend %font-size-sss;
    font-weight: bold;
  }
  .tooltip-date {
    fill: #979797;
    @extend %font-size-sss;
  }
  .tooltip-created-at {
    fill: #444444;
    @extend %font-size-sss;
  }
}
</style>